import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Telescope } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { Skeleton } from "@/components/ui/skeleton";
import AnomalyPanel from "@/components/platform/AnomalyPanel";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const GOLD_INK = "var(--brand-accent-ink)";
const TYPES = ["platform", "billing", "ai_gateway", "pii_reveal", "scheduler", "signup"];
const TYPE_STYLE = {
  platform: "bg-slate-100 border-slate-300 text-slate-700",
  billing: "bg-teal-50 border-teal-200 text-teal-700",
  ai_gateway: "bg-blue-50 border-blue-200 text-blue-700",
  pii_reveal: "bg-red-50 border-red-200 text-red-700",
  scheduler: "bg-purple-50 border-purple-200 text-purple-700",
  signup: "bg-orange-50 border-orange-200 text-orange-700",
};

export default function ObservatoryPanel({ tenants, onOpenTenant }) {
  const [events, setEvents] = useState(null);
  const [type, setType] = useState("all");
  const [tenant, setTenant] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    const params = { limit: 150 };
    if (type !== "all") params.type = type;
    if (tenant !== "all") params.tenant = tenant;
    try {
      const { data } = await platformApi.get("/observatory", { params });
      setEvents(data.events);
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, [type, tenant]); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="observatory-panel">
      <AnomalyPanel onAcknowledged={load} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#1E293B] flex items-center gap-2">
          <Telescope className="h-4 w-4" style={{ color: GOLD_INK }} /> Observatory
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-600">
            unified cross-tenant feed — read only
          </span>
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center" data-testid="observatory-type-filters">
            {["all", ...TYPES].map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`observatory-type-${t}`}
                className={`text-[10px] font-mono uppercase px-2 py-1 border -ml-px first:ml-0 transition-colors ${
                  type === t ? "bg-[#1E293B] border-[#1E293B] text-white"
                    : "border-slate-300 text-slate-500 hover:bg-slate-50"}`}>
                {t.replace("_", " ")}
              </button>
            ))}
          </div>
          <Select value={tenant} onValueChange={setTenant}>
            <SelectTrigger className="rounded-none h-8 w-40 text-[11px]" data-testid="observatory-tenant-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="text-[12px]">All tenants</SelectItem>
              <SelectItem value="platform" className="text-[12px]">platform</SelectItem>
              {tenants.map((t) => (
                <SelectItem key={t.subdomain} value={t.subdomain} className="text-[12px]">
                  {t.subdomain}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button onClick={load} disabled={refreshing} data-testid="observatory-refresh"
            className="text-[10px] font-mono border border-slate-300 px-2 py-1.5 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} refresh
          </button>
        </div>
      </div>

      {!events ? <Skeleton className="h-64 w-full rounded-none" /> : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="observatory-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                {["Time", "Tenant", "Type", "Actor", "Event"].map((h) => (
                  <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {events.map((ev, i) => (
                <tr key={ev.source_id || `${ev.at}-${i}`} data-testid={`observatory-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-[11px] text-slate-400 whitespace-nowrap">
                    {new Date(ev.at).toLocaleString("en-GB")}
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px]">
                    {ev.tenant_id && ev.tenant_id !== "platform" && ev.tenant_id !== "—" ? (
                      <button onClick={() => onOpenTenant(ev.tenant_id)}
                        data-testid={`observatory-tenant-link-${i}`}
                        className="underline decoration-dotted underline-offset-2 hover:text-[#1E293B]"
                        style={{ color: GOLD_INK }}>
                        {ev.tenant_id}
                      </button>
                    ) : <span className="text-slate-400">{ev.tenant_id || "—"}</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${TYPE_STYLE[ev.type] || ""}`}>
                      {ev.type.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px] text-slate-500">{ev.actor || "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{ev.summary}</td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400" data-testid="observatory-empty">
                  No events match this filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
